import { Link, useNavigate } from 'react-router-dom';
import { Heart, MapPin, Calendar, DollarSign, Users, ExternalLink, Palette, Music, Zap, Frame, Home, Tag, Leaf, Gift, FileText, CheckCircle2, Clock, Send, XCircle, Eye } from 'lucide-react';
import { EVENT_TYPES, formatDate, formatEventDates, formatCurrency, isDeadlineSoon, getDaysUntil } from '../lib/utils';
import { favoritesAPI, applicationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useState } from 'react';
import { Button } from './ui/button';

// Icon mapping for event types
const EVENT_ICONS = {
  Palette: Palette,
  Music: Music,
  Zap: Zap,
  Frame: Frame,
  Home: Home,
  Tag: Tag,
  Leaf: Leaf,
  Gift: Gift,
  Calendar: Calendar,
};

// Source display labels
const SOURCE_LABELS = {
  facebook: 'Facebook Events',
  eventbrite: 'Eventbrite',
  eventeny: 'Eventeny',
  zapp: 'ZAPP',
  google: 'Google',
  user_created: 'User Added',
  ai_discovery: 'Event Scout'
};

// Application status config
const APPLICATION_STATUS = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700', icon: Send },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  waitlisted: { label: 'Waitlisted', color: 'bg-purple-100 text-purple-700', icon: Clock },
};

export const EventCard = ({ 
  event, 
  distance, 
  onFavoriteChange, 
  showSource = false, 
  showApplyButton = false,
  applicationStatus = null,  // { id: string, status: string } or null
  onApplicationChange,
  isFavorite = false,
  sharedBy = null  // { name: string } - For community events to show who shared it
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isFavorited, setIsFavorited] = useState(event.is_favorited);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [currentAppStatus, setCurrentAppStatus] = useState(applicationStatus);

  const eventType = EVENT_TYPES[event.event_type] || { label: event.event_type, color: 'gray' };
  const deadlineSoon = isDeadlineSoon(event.deadline);
  const daysUntilDeadline = getDaysUntil(event.deadline);

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please log in to save events');
      return;
    }

    setIsLoading(true);
    try {
      if (isFavorited) {
        await favoritesAPI.remove(event.id);
        setIsFavorited(false);
        toast.success('Removed from My Events');
      } else {
        // Check if this is a preview event (from AI discovery, not yet saved)
        if (event.is_preview) {
          // Save the preview event to database and favorite it
          await favoritesAPI.savePreview({
            title: event.title,
            event_type: event.event_type,
            location: event.location,
            city: event.city,
            state: event.state,
            date: event.date,
            end_date: event.end_date,
            deadline: event.deadline,
            booth_cost: event.booth_cost,
            event_size: event.event_size,
            indoor_outdoor: event.indoor_outdoor,
            description: event.description,
            website_url: event.website_url,
            thumbnail_url: event.thumbnail_url,
            source: event.source,
            distance_miles: event.distance_miles
          });
          toast.success('Event saved to My Events!');
        } else {
          await favoritesAPI.add(event.id);
          toast.success('Added to My Events!');
        }
        setIsFavorited(true);
      }
      onFavoriteChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please log in to apply to events');
      navigate('/login');
      return;
    }

    setIsApplying(true);
    try {
      // First, ensure the event is saved (favorited)
      if (!isFavorited) {
        await favoritesAPI.add(event.id);
        setIsFavorited(true);
      }
      
      // Create the application with the default craft show template
      const response = await applicationsAPI.create({
        event_id: event.id,
        profile_id: null,
        task_template_id: 'template_craft_show',
        notes: null,
      });
      
      setCurrentAppStatus({ id: response.data.id, status: 'draft' });
      onApplicationChange?.();
      toast.success('Application created! Redirecting to manage tasks...');
      
      // Navigate to applications page
      setTimeout(() => {
        navigate('/applications');
      }, 500);
      
    } catch (error) {
      if (error.response?.data?.detail?.includes('already exists')) {
        toast.info('You already have an application for this event');
        navigate('/applications');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create application');
      }
    } finally {
      setIsApplying(false);
    }
  };

  const handleViewApplication = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/applications');
  };

  // Get the best thumbnail for the event
  // Priority: 1) event.image_url (user uploaded) 2) event.thumbnail_url (scraped from website) 3) gray placeholder
  const getEventThumbnail = () => {
    // If we have a custom uploaded image - highest priority
    if (event.image_url) {
      return { type: 'image', url: event.image_url };
    }
    // If we have a thumbnail from the website
    if (event.thumbnail_url) {
      return { type: 'image', url: event.thumbnail_url };
    }
    // No image available - use gray placeholder
    return { type: 'placeholder', url: null };
  };

  const thumbnail = getEventThumbnail();
  const appStatus = currentAppStatus ? APPLICATION_STATUS[currentAppStatus.status] : null;
  const StatusIcon = appStatus?.icon;
  const EventTypeIcon = eventType.icon && EVENT_ICONS[eventType.icon] ? EVENT_ICONS[eventType.icon] : Calendar;

  // For preview events, clicking should prompt to save first
  const handleCardClick = (e) => {
    if (event.is_preview) {
      e.preventDefault();
      // Trigger save action instead of navigation
      handleFavorite(e);
    }
    // Otherwise, let the Link handle the click
  };

  const CardWrapper = event.is_preview ? 'div' : Link;
  const cardProps = event.is_preview 
    ? { onClick: handleCardClick, className: "card-event block group cursor-pointer", "data-testid": `event-card-${event.id}` }
    : { to: `/events/${event.id}`, className: "card-event block group", "data-testid": `event-card-${event.id}` };

  return (
    <CardWrapper {...cardProps}>
      {/* Preview Badge */}
      {event.is_preview && (
        <div className="absolute top-2 left-2 z-20 bg-[#1E3A5F] text-white text-xs font-bold px-2 py-1 rounded-lg shadow-md">
          Click ❤️ to Save Event
        </div>
      )}
      
      {/* Image / Placeholder */}
      <div className="relative h-48 overflow-hidden">
        {thumbnail.type === 'image' ? (
          <img
            src={thumbnail.url}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              // On error, replace with gray placeholder
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        
        {/* Gray Placeholder (shown when no image or on image error) */}
        <div 
          className={`absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center ${thumbnail.type === 'image' ? 'hidden' : 'flex'}`}
        >
          <div className="text-center">
            <EventTypeIcon className="w-16 h-16 text-gray-400 mx-auto mb-2" />
            <span className="text-sm text-gray-500 font-medium">{eventType.label}</span>
          </div>
        </div>
        
        {/* Favorite Button */}
        <button
          onClick={handleFavorite}
          disabled={isLoading}
          className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center border-2 border-[#1E3A5F] transition-colors ${
            isFavorited
              ? 'bg-[#FE5F55] text-white'
              : 'bg-white text-[#1E3A5F] hover:bg-[#FE5F55] hover:text-white'
          }`}
          data-testid={`favorite-btn-${event.id}`}
        >
          <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
        </button>

        {/* Application Status Badge (top left, replaces deadline if app exists) */}
        {currentAppStatus && appStatus ? (
          <div className={`absolute top-3 left-3 ${appStatus.color} text-xs font-bold px-3 py-1 rounded-full border border-current flex items-center gap-1`}>
            <StatusIcon className="w-3 h-3" />
            {appStatus.label}
          </div>
        ) : daysUntilDeadline !== null && daysUntilDeadline < 0 ? (
          <div className="absolute top-3 left-3 bg-rose-100 text-rose-700 text-xs font-bold px-3 py-1 rounded-full border border-rose-400 flex items-center gap-1" data-testid={`deadline-passed-badge-${event.id}`}>
            <XCircle className="w-3 h-3" />
            Deadline Passed
          </div>
        ) : deadlineSoon && daysUntilDeadline >= 0 && (
          <div className="absolute top-3 left-3 bg-[#FE5F55] text-white text-xs font-bold px-3 py-1 rounded-full border border-[#1E3A5F]">
            {daysUntilDeadline === 0 ? 'Deadline Today!' : `${daysUntilDeadline} days left`}
          </div>
        )}

        {/* Category Badge with Icon */}
        <div className={`absolute bottom-3 left-3 badge-category badge-${event.event_type} flex items-center gap-1.5`}>
          {eventType.icon && EVENT_ICONS[eventType.icon] && (
            (() => {
              const IconComponent = EVENT_ICONS[eventType.icon];
              return <IconComponent className="w-3 h-3" />;
            })()
          )}
          {eventType.label}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-bold text-lg text-[#1E3A5F] mb-2 line-clamp-2 font-['Outfit']">
          {event.title}
        </h3>

        <div className="space-y-2 text-sm text-[#4A4A4A]">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#FE5F55]" />
            <span className="truncate">{event.city}, {event.state}</span>
          </div>
          
          {/* Event Dates - show all dates if multiple */}
          <div className="flex items-start gap-2">
            <Calendar className="w-4 h-4 text-[#FE5F55] mt-0.5 flex-shrink-0" />
            <div className="flex flex-col">
              {(() => {
                // For farmers markets with recurring schedule, show schedule pattern
                if (event.recurring_schedule?.computed_dates?.length > 0) {
                  const sched = event.recurring_schedule;
                  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                  const freqLabels = { every: 'Every', every_2nd: 'Every 2nd', every_3rd: 'Every 3rd', '1st_and_3rd': '1st & 3rd', '2nd_and_4th': '2nd & 4th' };
                  const daysStr = (sched.days || []).map(d => dayNames[d]).join(', ');
                  const freqStr = freqLabels[sched.frequency] || 'Every';
                  return (
                    <>
                      <span className="text-emerald-700 font-medium">{freqStr} {daysStr}</span>
                      <span className="text-xs text-[#71717A]">{formatDate(event.date)} - {formatDate(event.end_date)}</span>
                      <span className="text-xs text-emerald-600 font-medium">{sched.computed_dates.length} market days</span>
                    </>
                  );
                }
                
                const dates = formatEventDates(event);
                if (dates.length === 0) return <span>{formatDate(event.date)}</span>;
                if (dates.length === 1) return <span>{dates[0]}</span>;
                return (
                  <>
                    <span>{dates[0]}</span>
                    {dates.length > 1 && (
                      <span className="text-xs text-[#1E3A5F] font-medium">
                        +{dates.length - 1} more date{dates.length > 2 ? 's' : ''}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[#1E3A5F]" />
              <span className="font-semibold text-[#1E3A5F]">{formatCurrency(event.booth_cost)}</span>
            </div>
            
            {distance !== null && distance !== undefined && !isNaN(distance) ? (
              <div className="flex items-center gap-1 text-[#1E3A5F] font-semibold">
                <span className="text-sm">{distance} mi</span>
              </div>
            ) : event.event_size && (
              <div className="flex items-center gap-1 text-[#71717A]">
                <Users className="w-4 h-4" />
                <span className="capitalize text-xs">{event.event_size}</span>
              </div>
            )}
          </div>
        </div>

        {/* Website Link */}
        {event.website_url && (
          <span 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(event.website_url, '_blank', 'noopener,noreferrer');
            }}
            className="inline-flex items-center gap-1 text-sm text-[#1E3A5F] hover:text-[#1E3A5F] mt-3 font-medium cursor-pointer"
          >
            <ExternalLink className="w-4 h-4" />
            Visit Website
          </span>
        )}

        {/* Source Badge */}
        {showSource && event.source && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs text-[#71717A]">
              Found via: <span className="font-medium">{SOURCE_LABELS[event.source] || event.source}</span>
            </span>
          </div>
        )}

        {/* Community Event - Shared By Badge */}
        {sharedBy && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#1E3A5F]" />
              <span className="text-xs text-[#71717A]">
                Event Shared by <span className="font-medium text-[#1E3A5F]">{sharedBy.name || 'Community Member'}</span>
              </span>
            </div>
          </div>
        )}

        {/* Apply Button / Application Status */}
        {showApplyButton && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            {currentAppStatus ? (
              // Show application status and View button
              <div className="space-y-2">
                <div className={`flex items-center justify-center gap-2 py-2 px-4 rounded-lg ${appStatus?.color || 'bg-gray-100'}`}>
                  {StatusIcon && <StatusIcon className="w-4 h-4" />}
                  <span className="font-medium">Application: {appStatus?.label}</span>
                </div>
                <Button
                  onClick={handleViewApplication}
                  variant="outline"
                  className="w-full border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                  data-testid={`view-app-btn-${event.id}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Application & Tasks
                </Button>
              </div>
            ) : (
              // Show Apply Now button
              <Button
                onClick={handleApply}
                disabled={isApplying}
                className="w-full bg-[#1E3A5F] hover:bg-[#1E3A5F] text-white font-semibold"
                data-testid={`apply-btn-${event.id}`}
              >
                {isApplying ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1E3A5F] mr-2" />
                    Creating Application...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Apply Now
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </CardWrapper>
  );
};

export default EventCard;
